/**
 * OperationTitleDebtsModal — Títulos de dívida de uma operação de crédito.
 * Usado na tela de Operações de Crédito (CreditOperationsPage).
 */
import { useEffect, useState } from 'react';
import { FaTimes } from 'react-icons/fa';
import BadgeStatus from '../../../atoms/BadgeStatus';
import { listTitleDebts } from '../services/settlementApi';
import { formatBRL } from '../../../../utils/currencyUtils';
import { formatDateBR } from '../../../../utils/DateUtils';

type BadgeColor = 'blue' | 'green' | 'red' | 'orange' | 'gray' | 'yellow' | 'purple' | 'indigo' | 'pink';

const statusColors: Record<string, BadgeColor> = {
    OPEN: 'yellow',
    PAID: 'green',
    PARTIALLY_PAID: 'orange',
    SUPERSEDED: 'gray',
    CANCELLED: 'red',
    OVERPAID: 'purple',
};

const statusLabels: Record<string, string> = {
    OPEN: 'ABERTO',
    PAID: 'PAGO',
    PARTIALLY_PAID: 'PARCIALMENTE PAGO',
    SUPERSEDED: 'SUBSTITUÍDO',
    CANCELLED: 'CANCELADO',
    OVERPAID: 'PAGO EM EXCESSO',
};

const operationTypeLabels: Record<string, string> = {
    LOAN: 'Empréstimo',
    RECEIVABLES_ASSIGNMENT: 'Cessão de recebíveis',
    ACKNOWLEDGEMENT_OF_DEBT: 'Confissão de dívida',
    STRUCTURED_OPERATION: 'Operação estruturada',
};

const referenceLabel = (ref?: string) => {
    if (ref === 'INSTALLMENT') return 'Parcelas';
    if (ref === 'RECEIVABLE') return 'Recebíveis';
    return '-';
};

interface Props {
    operation: any;
    onClose: () => void;
}

const OperationTitleDebtsModal: React.FC<Props> = ({ operation, onClose }) => {
    const [rows, setRows] = useState<any[]>([]);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        (async () => {
            try {
                const data = await listTitleDebts({
                    'filter[credit_operation_id]': operation.credit_operation_id,
                    size: '200',
                    sort: 'due_date',
                });
                setRows(data.list || []);
            } catch { setRows([]); }
            setLoading(false);
        })();
    }, [operation.credit_operation_id]);

    return (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50" onClick={onClose}>
            <div className="bg-white dark:bg-slate-800 rounded-lg shadow-xl p-6 max-w-5xl w-full mx-4 max-h-[90vh] overflow-y-auto" onClick={e => e.stopPropagation()}>
                <div className="flex justify-between items-center mb-4">
                    <h2 className="text-lg font-semibold text-gray-900 dark:text-white">
                        Títulos de Dívida — {operation.contract_label || `${operation.contract_number || '?'}-${operation.amendment_number ?? 0}`}
                    </h2>
                    <button onClick={onClose} className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-200">
                        <FaTimes size={18} />
                    </button>
                </div>

                <div className="mb-4 grid grid-cols-4 gap-4 text-sm bg-gray-50 dark:bg-slate-700 p-3 rounded">
                    <div><span className="text-gray-500 dark:text-gray-400">Tipo:</span> <strong>{operationTypeLabels[operation.operation_type] || operation.operation_type || '-'}</strong></div>
                    <div><span className="text-gray-500 dark:text-gray-400">Valor Nominal:</span> <strong>{formatBRL(operation.nominal_value)}</strong></div>
                    <div><span className="text-gray-500 dark:text-gray-400">$ Recebido:</span> <strong className="text-green-600">{formatBRL(operation.paid_amount)}</strong></div>
                    <div><span className="text-gray-500 dark:text-gray-400">{referenceLabel(operation.business_reference)}:</span> <strong>{operation.title_debts_count ?? 0}</strong></div>
                </div>

                {loading ? (
                    <p className="text-center text-gray-500 py-4">Carregando...</p>
                ) : rows.length === 0 ? (
                    <p className="text-center text-gray-500 py-4">Nenhum título encontrado</p>
                ) : (
                    <table className="w-full text-sm">
                        <thead className="bg-gray-100 dark:bg-slate-700">
                            <tr className="text-left text-xs text-gray-500 dark:text-gray-400 uppercase">
                                <th className="px-3 py-2">#</th>
                                <th className="px-3 py-2 text-center">Parcela</th>
                                <th className="px-3 py-2 text-right">Valor Nominal</th>
                                <th className="px-3 py-2 text-right">$ Recebido</th>
                                <th className="px-3 py-2">Vencimento</th>
                                <th className="px-3 py-2">Status</th>
                            </tr>
                        </thead>
                        <tbody>
                            {rows.map((td: any) => (
                                <tr key={td.id} className="border-b border-gray-100 dark:border-slate-700">
                                    <td className="px-3 py-2 text-xs font-mono text-gray-500" title={td.charge_id || ''}>
                                        {td.charge_id ? String(td.charge_id).slice(0, 8) : '-'}
                                    </td>
                                    <td className="px-3 py-2 text-center">{td.installment_number || '-'}</td>
                                    <td className="px-3 py-2 text-right">{formatBRL(td.amount)}</td>
                                    <td className="px-3 py-2 text-right">{formatBRL(td.paid_amount)}</td>
                                    <td className="px-3 py-2">{formatDateBR(td.due_date)}</td>
                                    <td className="px-3 py-2">
                                        <BadgeStatus text={statusLabels[td.status] || td.status} color={statusColors[td.status] || 'gray'} />
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                )}
            </div>
        </div>
    );
};

export default OperationTitleDebtsModal;
